import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardMedia,
  IconButton,
  LinearProgress
} from '@mui/material';

import {
  CloudUpload,
  Delete,
  Image,
  VideoFile
} from '@mui/icons-material';

import SmartImage from '../utils/SmartImage';

export default function MediaUploader({ 
  mediaFile, 
  mediaPreview, 
  mediaType, 
  onFileSelect, 
  onRemove, 
  isUploading = false,
  uploadProgress = 0,
  error = null 
}) {

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && onFileSelect) {
      onFileSelect(file);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Mídia da Notícia
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!mediaFile && !mediaPreview && (
        <Card 
          sx={{ 
            p: 3, 
            textAlign: 'center', 
            border: '2px dashed #ccc',
            backgroundColor: '#fafafa',
            cursor: 'pointer',
            '&:hover': { backgroundColor: '#f0f0f0' }
          }}
          onClick={() => document.getElementById('media-upload-input').click()}
        >
          <CloudUpload sx={{ fontSize: 48, color: '#666', mb: 1 }} />
          <Typography variant="body1" color="textSecondary" gutterBottom>
            Clique para adicionar uma imagem ou vídeo
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Formatos aceitos: JPG, PNG, GIF, MP4, MOV, AVI
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Tamanho máximo: 5MB
          </Typography>
        </Card>
      )}

      {(mediaFile || mediaPreview) && (
        <Card sx={{ position: 'relative' }}>
          {mediaType === 'image' ? (
            mediaFile || (mediaPreview && mediaPreview.startsWith('blob:')) ? (
              <CardMedia
                component="img"
                height="200"
                image={mediaPreview}
                alt="Preview da mídia"
                sx={{ objectFit: 'cover' }}
              />
            ) : (
              <Box sx={{ height: 200, overflow: 'hidden' }}>
                <SmartImage
                  src={mediaPreview}
                  alt="Preview da mídia"
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'cover' 
                  }}
                  onError={(error) => console.error('❌ Erro ao carregar preview:', error)}
                  onLoad={() => console.log('✅ Preview carregado com sucesso')}
                />
              </Box>
            )
          ) : (
            <Box sx={{ 
              height: 200, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              backgroundColor: '#f5f5f5'
            }}>
              {mediaFile || (mediaPreview && mediaPreview.startsWith('blob:')) ? (
                <Box sx={{ textAlign: 'center' }}>
                  <VideoFile sx={{ fontSize: 64, color: '#666' }} />
                  <Typography variant="body2" color="textSecondary">
                    Vídeo: {mediaFile?.name || 'Arquivo de vídeo'}
                  </Typography>
                  {mediaFile && (
                    <Typography variant="caption" color="textSecondary">
                      {formatFileSize(mediaFile.size)}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Box sx={{ width: '100%', height: '100%', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Box>
                    <VideoFile sx={{ fontSize: 64, color: '#666', mb: 1 }} />
                    <Typography variant="body2" color="textSecondary">
                      Vídeo existente
                    </Typography>
                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                      Salvo no Google Drive
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* Botão de remoção */}
          <IconButton
            onClick={onRemove}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' }
            }}
            disabled={isUploading}
          >
            <Delete />
          </IconButton>

          {/* Overlay de upload */}
          {isUploading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white'
              }}
            >
              <CircularProgress color="inherit" sx={{ mb: 2 }} />
              <Typography variant="body2">
                Enviando para Google Drive...
              </Typography>
              {uploadProgress > 0 && (
                <Box sx={{ width: '80%', mt: 2 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={uploadProgress} 
                    sx={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.3)',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: 'white'
                      }
                    }}
                  />
                  <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                    {Math.round(uploadProgress)}%
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Informações do arquivo */}
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {mediaType === 'image' ? <Image /> : <VideoFile />}
              <Typography variant="body2">
                {mediaFile?.name || 'Mídia existente'}
              </Typography>
            </Box>
            {mediaFile && (
              <Typography variant="caption" color="textSecondary">
                {formatFileSize(mediaFile.size)} • {mediaType}
              </Typography>
            )}
          </Box>
        </Card>
      )}

      {/* Input oculto para upload */}
      <input
        id="media-upload-input"
        type="file"
        accept="image/*,video/*"
        onChange={handleFileChange}
        style={{ display: 'none' }}
        disabled={isUploading}
      />

      {/* Botão para alterar arquivo */}
      {(mediaFile || mediaPreview) && (
        <Button
          variant="outlined"
          startIcon={<CloudUpload />}
          onClick={() => document.getElementById('media-upload-input').click()}
          disabled={isUploading}
          sx={{ mt: 2 }}
        >
          Alterar Arquivo
        </Button>
      )}

    </Box>
  );
}
